import { createContext, useContext, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

/**
 * Who is on screen, when several things want the guest's attention at once.
 *
 * - **One top banner at a time.** Everything that drops in from the top (an alarm, a meeting,
 *   a verdict, FIXED or DAMAGE, a turn in the story, a message) asks for the slot with a
 *   priority. The highest one shows; the rest wait their turn, and come in when it leaves.
 * - **Covers.** Every sheet, and the alarm card when it is open, says it is covering the
 *   screen. A game underneath pauses, and the table game's card steps aside, while anything
 *   covers them.
 * - **The sheet layer** sits over the whole phone frame, so a sheet and its dimmed backdrop
 *   cover the header and the bars as well, and nothing behind can be tapped or scrolled.
 */

/** Higher shows first. */
export const PRIORITY = {
  fix: 60,
  alarm: 55,
  meeting: 50,
  offline: 45,
  verdict: 40,
  table: 35,
  flash: 30,
  story: 20,
  /** A message came in (chat/ChatCorner.tsx): below anything the show says. */
  chat: 15,
  toast: 10,
} as const;

interface BannerEntry {
  id: string;
  priority: number;
  node: ReactNode;
  order: number;
}

export class OverlayStore {
  private banners = new Map<string, BannerEntry>();
  private covers = new Set<string>();
  // Two channels, so the banners (which refresh on every render of whoever asked for them) never
  // re-render the screens that only care whether something covers them. Mixing the two loops.
  private bannerListeners = new Set<() => void>();
  private coverListeners = new Set<() => void>();
  private seq = 0;
  private bannerVersion = 0;

  setBanner(id: string, priority: number, node: ReactNode): void {
    const had = this.banners.get(id);
    this.banners.set(id, { id, priority, node, order: had?.order ?? ++this.seq });
    this.emitBanners();
  }

  removeBanner(id: string): void {
    if (this.banners.delete(id)) this.emitBanners();
  }

  /** The banner that has the slot: highest priority, then whoever asked first. */
  top(): BannerEntry | null {
    let best: BannerEntry | null = null;
    for (const entry of this.banners.values()) {
      if (!best || entry.priority > best.priority || (entry.priority === best.priority && entry.order < best.order)) {
        best = entry;
      }
    }
    return best;
  }

  setCover(id: string, on: boolean): void {
    const had = this.covers.has(id);
    if (on === had) return;
    if (on) this.covers.add(id);
    else this.covers.delete(id);
    for (const fn of this.coverListeners) fn();
  }

  coverCount(except?: string): number {
    return this.covers.size - (except && this.covers.has(except) ? 1 : 0);
  }

  /** Sheets only (not the alarm card): while one is open, banners wait their turn. */
  sheetCount(): number {
    let n = 0;
    for (const id of this.covers) if (id.startsWith('sheet:')) n += 1;
    return n;
  }

  subscribeBanners = (fn: () => void): (() => void) => {
    this.bannerListeners.add(fn);
    return () => this.bannerListeners.delete(fn);
  };

  bannerSnapshot = (): number => this.bannerVersion;

  subscribeCovers = (fn: () => void): (() => void) => {
    this.coverListeners.add(fn);
    return () => this.coverListeners.delete(fn);
  };

  private emitBanners(): void {
    this.bannerVersion += 1;
    for (const fn of this.bannerListeners) fn();
  }
}

const OverlayContext = createContext<OverlayStore | null>(null);
const LayerContext = createContext<HTMLElement | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new OverlayStore());
  return <OverlayContext.Provider value={store}>{children}</OverlayContext.Provider>;
}

/** Where sheets are drawn: a layer over the whole phone frame (see Shell). */
export const SheetLayerProvider = LayerContext.Provider;
export function useSheetLayer(): HTMLElement | null {
  return useContext(LayerContext);
}

function useStore(): OverlayStore {
  const store = useContext(OverlayContext);
  if (!store) throw new Error('OverlayProvider is missing');
  return store;
}

/**
 * Ask for the top slot. Pass `null` (or false) when there is nothing to show. The node is
 * refreshed on every render, so a countdown inside it keeps ticking.
 */
export function useBanner(id: string, priority: number, node: ReactNode | null | false): void {
  const store = useStore();
  useLayoutEffect(() => {
    if (node) store.setBanner(id, priority, node);
    else store.removeBanner(id);
  });
  useLayoutEffect(() => () => store.removeBanner(id), [id, store]);
}

/** Say that this is covering the screen while `active`. */
export function useCover(id: string, active: boolean): void {
  const store = useStore();
  useLayoutEffect(() => {
    store.setCover(id, active);
    return () => store.setCover(id, false);
  }, [id, active, store]);
}

/** How many things are covering the screen, not counting `except`. */
export function useCoverCount(except?: string): number {
  const store = useStore();
  return useSyncExternalStore(store.subscribeCovers, () => store.coverCount(except));
}

/**
 * The top slot, at the very top of the phone frame, over everything. While a sheet
 * is open nothing drops in over it: banners wait until it closes (the app closes CONTROLS by
 * itself when an alarm needs this phone's FIX).
 */
export function BannerHost() {
  const store = useStore();
  useSyncExternalStore(store.subscribeBanners, store.bannerSnapshot);
  const sheets = useSyncExternalStore(store.subscribeCovers, () => store.sheetCount());
  const top = store.top();
  const shown = top !== null && sheets === 0;
  // How much room the banner takes, as --banner-h: an open game moves down by it, so a banner never
  // lies over the game's title or instructions (Marek, 2026-09-26: no text over text).
  const box = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = box.current;
    if (!shown || !el) {
      root.style.setProperty('--banner-h', '0px');
      return;
    }
    const set = () => root.style.setProperty('--banner-h', `${Math.ceil(el.getBoundingClientRect().height) + 12}px`);
    set();
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(set);
    watch?.observe(el);
    return () => {
      watch?.disconnect();
      root.style.setProperty('--banner-h', '0px');
    };
  }, [shown, top?.id]);
  if (!shown) return null;
  return (
    <div
      ref={box}
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top) + 6px)',
        left: 8,
        right: 8,
        zIndex: 90,
        pointerEvents: 'none',
      }}
    >
      <div key={top.id} style={{ pointerEvents: 'auto' }}>
        {top.node}
      </div>
    </div>
  );
}
